import React, { useEffect, useMemo, useState } from 'react';
import SunCalc from 'suncalc';
import { Sunrise, Sunset, Sparkles } from 'lucide-react';

/**
 * TonightsSky
 * A celestial widget showing:
 *  - Current moon phase (name, illumination %, visual icon)
 *  - Tonight's moonrise + moonset times (local, in CT for the ranch)
 *  - Active meteor shower (if any), with peak date
 *
 * No API key required — uses SunCalc (deterministic astro math)
 * and a hard-coded annual meteor shower calendar.
 *
 * Coordinates: Lovelady, TX  31.1257°N, 95.4527°W
 */

const RANCH_LAT = 31.1257;
const RANCH_LNG = -95.4527;

// Annual meteor showers (UTC dates). Showers crossing year boundaries handled below.
const METEOR_SHOWERS = [
  { name: 'Quadrantids',   start: '01-01', end: '01-12', peak: '01-04', zhr: 110 },
  { name: 'Lyrids',        start: '04-16', end: '04-30', peak: '04-22', zhr: 18 },
  { name: 'Eta Aquariids', start: '04-19', end: '05-28', peak: '05-06', zhr: 50 },
  { name: 'Perseids',      start: '07-17', end: '08-24', peak: '08-12', zhr: 100 },
  { name: 'Orionids',      start: '10-02', end: '11-07', peak: '10-21', zhr: 20 },
  { name: 'Leonids',       start: '11-06', end: '11-30', peak: '11-17', zhr: 15 },
  { name: 'Geminids',      start: '12-04', end: '12-20', peak: '12-14', zhr: 150 },
  { name: 'Ursids',        start: '12-17', end: '12-26', peak: '12-22', zhr: 10 },
];

// Convert a "MM-DD" string to a Date in the given year
const dateAt = (year, mmdd) => {
  const [m, d] = mmdd.split('-').map(Number);
  return new Date(year, m - 1, d);
};

const findActiveShower = (today) => {
  const year = today.getFullYear();
  for (const s of METEOR_SHOWERS) {
    const start = dateAt(year, s.start);
    const end = dateAt(year, s.end);
    end.setHours(23, 59, 59);
    if (today >= start && today <= end) {
      return { ...s, peakDate: dateAt(year, s.peak) };
    }
  }
  return null;
};

// Friendly moon-phase name from illumination (0-1) + waxing/waning hint
const phaseNameFromValue = (phase) => {
  // SunCalc phase: 0 = New, 0.25 = First Quarter, 0.5 = Full, 0.75 = Last Quarter
  if (phase < 0.03 || phase > 0.97) return 'New Moon';
  if (phase < 0.22) return 'Waxing Crescent';
  if (phase < 0.28) return 'First Quarter';
  if (phase < 0.47) return 'Waxing Gibbous';
  if (phase < 0.53) return 'Full Moon';
  if (phase < 0.72) return 'Waning Gibbous';
  if (phase < 0.78) return 'Last Quarter';
  return 'Waning Crescent';
};

// SVG moon icon that visually matches the current phase
const MoonIcon = ({ phase, size = 64 }) => {
  // phase: 0..1
  // We draw a full white disc and overlay a dark crescent that shifts horizontally.
  // Width of the dark overlay is illuminated portion's complement, anchored by waxing/waning.
  const illum = phase <= 0.5 ? phase * 2 : (1 - phase) * 2; // 0..1
  const waxing = phase < 0.5;
  const r = size / 2;
  // The dark ellipse shifts based on phase. Compute horizontal offset of the terminator.
  // Mask using two arcs (a clipping ellipse) for a realistic terminator.
  const k = 1 - 2 * illum; // -1 (full) to 1 (new)
  const ellipseRx = Math.abs(k) * r;
  // If waxing, dark side is on the left; if waning, on the right
  // We render an ellipse that "carves out" the dark part by being colored as background
  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      aria-hidden="true"
      className="drop-shadow-[0_0_18px_rgba(229,193,88,0.45)]"
    >
      <defs>
        <radialGradient id="moon-grad" cx="38%" cy="35%" r="65%">
          <stop offset="0%" stopColor="#FFF6DA" />
          <stop offset="55%" stopColor="#E5C158" />
          <stop offset="100%" stopColor="#B8860B" />
        </radialGradient>
        <clipPath id="moon-clip">
          <circle cx={r} cy={r} r={r - 0.5} />
        </clipPath>
      </defs>
      {/* Lit disc */}
      <circle cx={r} cy={r} r={r - 0.5} fill="url(#moon-grad)" />
      {/* Dark crescent overlay (covers the unilluminated half) */}
      <g clipPath="url(#moon-clip)">
        {/* Half disc on the dark side */}
        <rect
          x={waxing ? 0 : r}
          y={0}
          width={r}
          height={size}
          fill="#0B132B"
        />
        {/* Terminator ellipse — fills with the colour of whichever side it's adjusting */}
        {ellipseRx > 0.3 && (
          <ellipse
            cx={r}
            cy={r}
            rx={ellipseRx}
            ry={r - 0.5}
            fill={k > 0 ? '#0B132B' : 'url(#moon-grad)'}
          />
        )}
      </g>
    </svg>
  );
};

const fmtTime = (date) => {
  if (!date || isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/Chicago',
  });
};

const fmtPeakDate = (date) =>
  date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'America/Chicago',
  });

export const TonightsSky = () => {
  const [now, setNow] = useState(new Date());

  // Refresh every 5 minutes so phase/rise/set stay current for long-open tabs
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  const data = useMemo(() => {
    const phaseData = SunCalc.getMoonIllumination(now);
    const times = SunCalc.getMoonTimes(now, RANCH_LAT, RANCH_LNG);
    const phaseName = phaseNameFromValue(phaseData.phase);
    const illumPct = Math.round(phaseData.fraction * 100);
    const shower = findActiveShower(now);
    return { phase: phaseData.phase, phaseName, illumPct, times, shower };
  }, [now]);

  return (
    <section
      data-testid="tonights-sky-widget"
      aria-label="Tonight's sky at Creek Bottom Ranch"
      className="relative py-20 bg-[#050814] border-y border-[#B8860B]/15"
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <p className="uppercase tracking-[0.4em] text-[10px] md:text-xs text-[#D4AF37] mb-3">
            From the ranch, right now
          </p>
          <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#F4F1EA] mb-3">
            Tonight's Sky
          </h2>
          <p className="text-sm md:text-base text-[#F4F1EA]/60 max-w-xl mx-auto">
            What you'll see overhead at Lovelady, TX tonight.
          </p>
        </div>

        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Moon phase card */}
          <div
            data-testid="tonights-sky-moon"
            className="relative overflow-hidden rounded-xl bg-gradient-to-b from-[#0B132B] to-[#050814] border border-[#B8860B]/25 p-7 flex flex-col items-center text-center group hover:border-[#B8860B]/55 transition-colors"
          >
            <div className="absolute inset-0 opacity-50 pointer-events-none"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 18% 22%, #E6E8FA 0.6px, transparent 1.4px), radial-gradient(circle at 78% 60%, #E5C158 0.7px, transparent 1.5px), radial-gradient(circle at 35% 85%, #FFFFFF 0.6px, transparent 1.3px)',
                backgroundSize: '160px 120px',
              }}
            />
            <p className="relative text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-4">
              Moon phase
            </p>
            <div className="relative mb-4 transition-transform duration-500 group-hover:scale-[1.04]">
              <MoonIcon phase={data.phase} size={88} />
            </div>
            <p className="relative font-display text-2xl text-[#F4F1EA]">{data.phaseName}</p>
            <p className="relative text-sm text-[#F4F1EA]/65 mt-1">
              {data.illumPct}% illuminated
            </p>
          </div>

          {/* Moonrise / Moonset card */}
          <div
            data-testid="tonights-sky-times"
            className="relative rounded-xl bg-gradient-to-b from-[#0B132B] to-[#050814] border border-[#B8860B]/25 p-7 flex flex-col justify-center hover:border-[#B8860B]/55 transition-colors"
          >
            <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-5 text-center">
              Moon tonight (CT)
            </p>
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-full bg-[#B8860B]/10 border border-[#B8860B]/40 flex items-center justify-center flex-shrink-0">
                  <Sunrise className="text-[#D4AF37]" size={20} />
                </span>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-[#F4F1EA]/55">Moonrise</p>
                  <p className="font-display text-2xl text-[#F4F1EA]">{fmtTime(data.times.rise)}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="w-11 h-11 rounded-full bg-[#B8860B]/10 border border-[#B8860B]/40 flex items-center justify-center flex-shrink-0">
                  <Sunset className="text-[#D4AF37]" size={20} />
                </span>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-[#F4F1EA]/55">Moonset</p>
                  <p className="font-display text-2xl text-[#F4F1EA]">{fmtTime(data.times.set)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Meteor shower card */}
          <div
            data-testid="tonights-sky-shower"
            className={`relative overflow-hidden rounded-xl border p-7 flex flex-col justify-center text-center hover:border-[#B8860B]/55 transition-colors ${
              data.shower
                ? 'bg-gradient-to-br from-[#0B132B] via-[#0B132B] to-[#1a2547] border-[#B8860B]/40'
                : 'bg-gradient-to-b from-[#0B132B] to-[#050814] border-[#B8860B]/20'
            }`}
          >
            <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-4">
              Meteor shower
            </p>
            {data.shower ? (
              <>
                <Sparkles className="text-[#D4AF37] mx-auto mb-3 animate-pulse" size={32} />
                <p className="font-display text-2xl text-[#F4F1EA]">{data.shower.name}</p>
                <p className="text-sm text-[#F4F1EA]/65 mt-2">
                  Peaks {fmtPeakDate(data.shower.peakDate)} · up to {data.shower.zhr}/hr
                </p>
                <p className="text-xs text-[#E5C158]/80 mt-2 italic">
                  Active overhead tonight
                </p>
              </>
            ) : (
              <>
                <Sparkles className="text-[#F4F1EA]/30 mx-auto mb-3" size={32} />
                <p className="font-display text-xl text-[#F4F1EA]/80">No major shower</p>
                <p className="text-sm text-[#F4F1EA]/55 mt-2">
                  Just constellations and quiet sky tonight.
                </p>
              </>
            )}
          </div>
        </div>

        <p className="text-center mt-8 text-xs text-[#F4F1EA]/40">
          Astronomical data for 31.13°N, 95.45°W · refreshes every five minutes
        </p>
      </div>
    </section>
  );
};

export default TonightsSky;
